
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

import { CsvFileService } from './csv-file.service';
import { retry } from 'rxjs/operators';
import { Observable, forkJoin, of } from 'rxjs';
import { UploadType } from '../_enums/uploads/upload-type.enum';
import { ProductInformationsUpload } from '../pages/upload-csv/models/product-informations-upload';
import { ProductTagsUpload } from '../pages/upload-csv/models/product-tags-upload';
import { ProductActiveStatusUpload } from '../pages/upload-csv/models/product-active-status-upload';
import { SuppliersUpload } from '../pages/upload-csv/models/suppliers-upload';
import { CaseConfigurationsUpload } from '../pages/upload-csv/models/case-configurations-upload';
import { MainsAndSubsUpload } from '../pages/upload-csv/models/mains-and-subs-upload';
import { InventoryUpload } from '../pages/upload-csv/models/inventory-upload';
import { KitsUpload } from '../pages/upload-csv/models/kits-upload';
import { FileUploadModel } from '../pages/upload-csv/models/file-upload.model';
import { OrderInformationUpload } from '../pages/upload-csv/models/order-upload';
import { BoxConfigsUpload } from '../pages/upload-csv/models/box-configs';
import { AdditionalFeeUpload } from '../pages/upload-csv/models/additional-fees';
import * as uuid from 'uuid';

const API_IMPORT_URL = environment.baseUrl + '/import';
// Real REST API
@Injectable()
export class ImportService {
  constructor(
    private http: HttpClient,
    private papaService: CsvFileService
  ) { }

  private files: any[];

  setFiles(files: any[]) {
    this.files = files;
  }

  getFiles() {
    return this.files;
  }

  importInventory(items: InventoryUpload.PayloadItem[], fileName: string, locationId: number, adjustmentDate: Date = null) {
    const metaData = {
      locationId,
      adjustmentDate
    };
    return this.processUploads(items, fileName, UploadType.ProductInventory, metaData);
  }

  importMainsSubs(items: MainsAndSubsUpload.PayloadItem[], fileName: string) {
    return this.processUploads(items, fileName, UploadType.ProductMainsAndSubs);
  }

  importKits(items: KitsUpload.PayloadItem[], fileName: string) {
    return this.processUploads(items, fileName, UploadType.ProductKits);
  }

  importProductInformation(items: ProductInformationsUpload.PayloadItem[], fileName: string) {
    return this.processUploads(items, fileName, UploadType.ProductInformation);
  }

  importOrderInformation(items: OrderInformationUpload.PayloadItem[], fileName: string) {
    return this.processUploads(items, fileName, UploadType.OrderInformation);
  }

  importCaseConfiguration(items: CaseConfigurationsUpload.PayloadItem[], fileName: string) {
    return this.processUploads(items, fileName, UploadType.ProductCaseConfigurations);
  }

  importProductTag(items: ProductTagsUpload.PayloadItem[], fileName: string) {
    return this.processUploads(items, fileName, UploadType.ProductTags);
  }

  importProductActiveStatus(items: ProductActiveStatusUpload.PayloadItem[], fileName: string) {
    return this.processUploads(items, fileName, UploadType.ProductActiveStatus);
  }

  importSuppliers(items: SuppliersUpload.PayloadItem[], fileName: string) {
    return this.processUploads(items, fileName, UploadType.ProductSuppliers);
  }

  importBoxConfigs(items: BoxConfigsUpload.PayloadItem[], fileName: string) {
    return this.processUploads(items, fileName, UploadType.ProductPackageBoxConfigurations);
  }

  importAdditionalFee(items: AdditionalFeeUpload.PayloadItem[], fileName: string) {
    return this.processUploads(items, fileName, UploadType.OrderAdditionalFees);
  }

  importTargetItemTypes(items: unknown[], fileName: string) {
    return this.processUploads(items, fileName, UploadType.TargetItemTypes);
  }

  importTargetProductMappings(items: unknown[], fileName: string) {
    return this.processUploads(items, fileName, UploadType.TargetProductMappings);
  }

  importTargetReferralFees(items: unknown[], fileName: string) {
    return this.processUploads(items, fileName, UploadType.TargetReferralFees);
  }

  importAdvertisingCosts(items: unknown[], fileName: string, storeId: number, periodStart?: string,
    periodEnd?: string, replacePeriod = false) {
    // One batch id shared by every chunk of this file. The backend accumulates rows within a
    // batch and replaces rows from an earlier batch, so a corrected re-upload corrects the
    // figures instead of doubling them. No retry: a silently retried chunk that actually
    // succeeded would add its spend twice, and a visible failure is re-uploadable safely.
    // batchStartedAt is shared by every chunk and decides which of two overlapping uploads
    // wins a day, so an older upload still finishing cannot clobber a newer one.
    // periodStart/periodEnd are the period the file covers. When set they replace the row date
    // columns, because a campaign report's dates describe the campaign, not the spend window.
    const metaData = {
      storeId, batchId: uuid.v4(), batchStartedAt: new Date().toISOString(),
      periodStart: periodStart ?? '', periodEnd: periodEnd ?? '',
      // Opt-in: also clears spend in the period that this file no longer mentions.
      replacePeriod: replacePeriod ? 'true' : 'false'
    };
    const csvChunks = this.papaService.toCsvChunks(items, fileName, 1000);
    const observables = csvChunks.map(formData => {
      formData.set("uploadType", UploadType.AdvertisingCosts);
      formData.set("metaData", JSON.stringify(metaData));
      return this.http.post(`${environment.baseUrl}/import/advertisingCosts`, formData, { reportProgress: true });
    });
    return observables.length ? forkJoin(observables) : of([]);
  }

  importAdvertisingCampaignMappings(items: unknown[], fileName: string, storeId: number) {
    const metaData = { storeId };
    const csvChunks = this.papaService.toCsvChunks(items, fileName, 1000);
    const observables = csvChunks.map(formData => {
      formData.set("uploadType", UploadType.AdvertisingCampaignMappings);
      formData.set("metaData", JSON.stringify(metaData));
      return this.http.post(`${environment.baseUrl}/import/advertisingCampaignMappings`, formData, { reportProgress: true });
    });
    return observables.length ? forkJoin(observables) : of([]);
  }

  importReturnsByOrderNumber(items: unknown[], fileName: string, storeId: number) {
    const metaData = { storeId };
    const csvChunks = this.papaService.toCsvChunks(items, fileName, 1000);
    const observables = csvChunks.map(formData => {
      formData.set("uploadType", UploadType.ReturnsByOrderNumber);
      formData.set("metaData", JSON.stringify(metaData));
      return this.http.post(`${environment.baseUrl}/import/returns`, formData, { reportProgress: true }).pipe(retry(3));
    });
    return observables.length ? forkJoin(observables) : of([]);
  }

  processUploads(items: unknown[], fileName: string, uploadType: UploadType, metaData: Record<string, unknown> = null) {
    const csvChunks = this.papaService.toCsvChunks(items, fileName, 1000);
    const observables = csvChunks.map(formData => {
      formData.set("uploadType", UploadType[uploadType]);
      if (metaData) {
        formData.set("metaData", JSON.stringify(metaData));
      }
      return this.http.post(`${API_IMPORT_URL}`, formData, { reportProgress: true }).pipe(retry(3));
    });
    return forkJoin(observables);
  }

  uploadHistory(): Observable<FileUploadModel[]> {
    return this.http.get<FileUploadModel[]>(`${API_IMPORT_URL}/uploadHistory`);
  }

  downloadImportedFile(id: number) {
    return this.http.get(`${API_IMPORT_URL}/downloadImportedFile/${id}`, { responseType: 'text' });
  }
}
